// Pulsante floating "cliente non gestito": compare al PM quando un cliente gli è
// stato assegnato da più di 24 ore e non ha ancora nessun task (la regola sta
// nel backend, routes/presa_in_carico.py). Al tap porta al calendario filtrato
// su quel cliente con il dialog di creazione task già aperto; con più clienti
// apre prima un piccolo elenco per scegliere.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, X } from "lucide-react";
import { apiFetch } from "../utils/api";

const POLLING_MS = 5 * 60 * 1000;

export default function ClientiNonGestitiFab() {
  const navigate = useNavigate();
  const location = useLocation();
  const [clienti, setClienti] = useState([]);
  const [aperto, setAperto] = useState(false);
  const pannelloRef = useRef(null);

  const carica = useCallback(async () => {
    try {
      const res = await apiFetch("/presa-in-carico/non-gestiti");
      if (res.ok) setClienti((await res.json()).clienti || []);
    } catch {
      // Silenzioso: è un promemoria, non deve disturbare se la rete non va.
    }
  }, []);

  // Ricarica a ogni cambio pagina (economico, e dopo aver creato un task il
  // pulsante sparisce subito), più un polling lento per chi resta sulla stessa pagina.
  useEffect(() => { carica(); }, [carica, location.pathname]);
  useEffect(() => {
    const id = setInterval(carica, POLLING_MS);
    window.addEventListener("presa-in-carico:aggiorna", carica);
    return () => {
      clearInterval(id);
      window.removeEventListener("presa-in-carico:aggiorna", carica);
    };
  }, [carica]);

  useEffect(() => {
    if (!aperto) return undefined;
    const chiudi = (e) => {
      if (pannelloRef.current && !pannelloRef.current.contains(e.target)) setAperto(false);
    };
    document.addEventListener("mousedown", chiudi);
    return () => document.removeEventListener("mousedown", chiudi);
  }, [aperto]);

  if (clienti.length === 0) return null;

  const vaiAlCliente = (clientId) => {
    setAperto(false);
    navigate(`/calendar?client=${clientId}&nuovo_task=1`);
  };

  const onTap = () => {
    if (clienti.length === 1) vaiAlCliente(clienti[0].client_id);
    else setAperto(v => !v);
  };

  const etichetta = clienti.length === 1
    ? `${clienti[0].name} non è ancora gestito`
    : `${clienti.length} clienti non ancora gestiti`;

  return (
    // bottom-24 su mobile per stare sopra la bottom nav (h-16).
    <div ref={pannelloRef} className="fixed right-4 bottom-24 lg:right-8 lg:bottom-8 z-40 scheda-font-ui flex flex-col items-end gap-2">
      {aperto && (
        <div className="w-72 max-w-[calc(100vw-2rem)] rounded-[18px] border border-[#e9e9f1] bg-white shadow-lg p-1.5" data-testid="clienti-non-gestiti-elenco">
          <div className="flex items-center justify-between px-2.5 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9095a1]">Clienti senza task</span>
            <button type="button" onClick={() => setAperto(false)} className="text-[#9095a1] hover:text-[#565d6d]" aria-label="Chiudi">
              <X className="w-4 h-4" />
            </button>
          </div>
          {clienti.map(c => (
            <button
              key={c.client_id}
              type="button"
              onClick={() => vaiAlCliente(c.client_id)}
              className="w-full flex items-center justify-between gap-2 rounded-[10px] px-2.5 py-2 text-left text-[13px] font-semibold text-[#15161c] hover:bg-[#f3f3f8]"
            >
              <span className="truncate">{c.name}</span>
              <ChevronRight className="w-4 h-4 text-[#9095a1] shrink-0" />
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={onTap}
        data-testid="clienti-non-gestiti-fab"
        className="max-w-[calc(100vw-2rem)] h-12 pl-3.5 pr-4 rounded-full bg-[#b45309] hover:bg-[#9a4708] text-white shadow-lg inline-flex items-center gap-2 text-[13px] font-semibold transition-colors"
      >
        <span className="relative flex w-6 h-6 items-center justify-center rounded-full bg-white/20 shrink-0">
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.2} />
          <span className="absolute inset-0 rounded-full bg-white/30 animate-ping motion-reduce:animate-none" />
        </span>
        <span className="truncate">{etichetta}</span>
      </button>
    </div>
  );
}
